import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService, TreeNode } from 'primeng/api';
import { TreeModule } from 'primeng/tree';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, forkJoin } from 'rxjs';
import { ChopMassnahme, ChopMesskriteriumMassnahmenRegel } from '@entity/chop';
import { ChopService } from '@service/chop.service';
import { FormNumberInputComponent } from '@shared/form-number-input/form-number-input.component';
import { buildMassnahmenTree, currentLanguage, showTacsCodeKreisError } from '../../massnahmen-tree';

@Component({
  selector: 'app-chop-regel-dialog',
  templateUrl: './chop-regel-dialog.component.html',
  styleUrls: ['./chop-regel-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, TreeModule, ButtonModule, TranslatePipe, FormNumberInputComponent],
})
export class ChopRegelDialogComponent {
  private formBuilder = inject(FormBuilder);
  private dialogConfig = inject(DynamicDialogConfig);
  private dialogRef = inject(DynamicDialogRef);
  private chopService = inject(ChopService);
  private translateService = inject(TranslateService);
  private messageService = inject(MessageService);

  private messkriteriumId: number = this.dialogConfig.data.messkriteriumId;
  private regel: ChopMesskriteriumMassnahmenRegel | undefined = this.dialogConfig.data.regel;
  isEdit = !!this.regel;
  form = this.formBuilder.group({
    maximalAnzahl: [this.regel?.maximalAnzahl ?? null],
    mindestAnzahl: [this.regel?.mindestAnzahl ?? null],
  });
  treeNodes: TreeNode[] = buildMassnahmenTree(
    this.dialogConfig.data.allMassnahmen,
    currentLanguage(this.translateService),
    {
      disabledIds: new Set<number>(this.dialogConfig.data.alreadyAssignedIds),
      visibleIds: this.regel ? undefined : new Set<number>(this.dialogConfig.data.profilMassnahmenIds),
    }
  );
  selection: TreeNode<ChopMassnahme>[] = this.regel
    ? [this.findNodeById(this.treeNodes, this.regel.tacsCodeKreisId)!]
    : [];
  submitting = false;

  onSubmit(): void {
    const tacsCodeKreisIds = this.regel
      ? [this.regel.tacsCodeKreisId]
      : this.selection.filter((n) => n.data?.isMassnahme).map((n) => n.data!.id);
    if (tacsCodeKreisIds.length === 0 || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    this.submitting = true;
    forkJoin(
      tacsCodeKreisIds.map((tacsCodeKreisId) => {
        const regelPayload: ChopMesskriteriumMassnahmenRegel = {
          id: this.regel?.id ?? 0,
          chopMesskriteriumId: this.messkriteriumId,
          tacsCodeKreisId,
          maximalAnzahl: value.maximalAnzahl as number | null,
          mindestAnzahl: value.mindestAnzahl as number | null,
        };
        return this.regel
          ? this.chopService.updateRegel(this.regel.id, regelPayload)
          : this.chopService.createRegel(this.messkriteriumId, regelPayload);
      })
    )
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (results) => this.dialogRef.close(results),
        error: (err) => showTacsCodeKreisError(this.messageService, this.translateService, err),
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private findNodeById(nodes: TreeNode[], id: number): TreeNode | null {
    for (const node of nodes) {
      if ((node.data as ChopMassnahme | undefined)?.id === id) {
        return node;
      }
      const found = this.findNodeById(node.children ?? [], id);
      if (found) {
        return found;
      }
    }
    return null;
  }
}
